import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { hasPerm } from '../../utils/permissions';
import { db } from '../../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import PortalHeader from '../../components/Portal/PortalHeader';
import EmailComposer from '../../components/EmailFamilies/EmailComposer';
import '../../components/Portal/portalSurface.css';
import '../../components/EmailFamilies/EmailFamilies.css';

const STATUS_BADGE = {
	sent: 'success',
	partial: 'warning',
	failed: 'danger',
};

const formatSentAt = (ts) => {
	if (!ts) return '';
	const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
	return date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
};

// Admin/owner-only history of every staff-composed email. Reads sentEmails
// (rules already restrict reads to admin/owner, so this never renders for a
// non-privileged staff member).
const SentLog = () => {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	const load = useCallback(async () => {
		setLoading(true);
		setError('');
		try {
			const q = query(
				collection(db, 'sentEmails'),
				orderBy('sentAt', 'desc'),
				limit(50)
			);
			const snap = await getDocs(q);
			setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
		} catch (err) {
			console.error('Error loading sent log:', err);
			setError(err.message || 'Failed to load the sent log.');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	return (
		<div className="ps-card mt-4">
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h2 className="ps-card__title mb-0">
					<i className="fa-sharp fa-solid fa-clock-rotate-left" /> Sent mail log
				</h2>
				<button type="button" className="ps-btn" onClick={load} disabled={loading}>
					<i className="fa-sharp fa-solid fa-rotate-right" /> Refresh
				</button>
			</div>

			{error && (
				<div className="ps-alert ps-alert--danger">
					<i className="fa-sharp fa-solid fa-triangle-exclamation me-2" />
					{error}
				</div>
			)}

			{loading ? (
				<div className="ps-spinner" role="status" aria-label="Loading" />
			) : rows.length === 0 ? (
				<div className="ps-alert ps-alert--info">
					<i className="fa-sharp fa-solid fa-inbox me-2" />
					No emails have been sent yet.
				</div>
			) : (
				<div className="ef-log">
					{rows.map((row) => (
						<div className="ef-log__row" key={row.id}>
							<div className="ef-log__top">
								<span className="ef-log__subject">{row.subject || '(no subject)'}</span>
								<span className={`ps-badge ps-badge--${STATUS_BADGE[row.status] || 'neutral'}`}>
									{row.status || 'sent'}
								</span>
							</div>
							<div className="ef-log__meta">
								{formatSentAt(row.sentAt)}
								{row.sentByName ? ` · ${row.sentByName}` : ''}
							</div>
							<div className="ef-log__to">
								<i className="fa-sharp fa-solid fa-users me-1" />
								{(row.toEmails || []).join(', ')}
							</div>
							{row.error && (
								<div className="ef-log__to" style={{ color: '#b23b3b' }}>
									<i className="fa-sharp fa-solid fa-triangle-exclamation me-1" />
									{row.error}
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const EmailFamilies = () => {
	const { userProfile } = useAuth();
	const canSend = hasPerm(userProfile, 'sendEmail');
	const isAdminOrOwner =
		userProfile?.role === 'admin' || userProfile?.owner === true;

	if (!canSend) {
		return (
			<>
				<PortalHeader title="Email Families" />
				<div className="ps-page email-families-page">
					<div className="ps-alert ps-alert--warning">
						<i className="fa-sharp fa-solid fa-lock me-2" />
						<strong>No access.</strong> You don't have permission to email families.
						Ask an administrator to enable it in Staff Access.
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<PortalHeader
				title="Email Families"
				subtitle="Send a one-off email to a family or student as sing@coloryourvoice.com"
			/>
			<div className="ps-page email-families-page">
				<div className="ps-card">
					<h2 className="ps-card__title mb-3">
						<i className="fa-sharp fa-solid fa-paper-plane" /> Compose email
					</h2>
					<EmailComposer />
				</div>

				{isAdminOrOwner && <SentLog />}
			</div>
		</>
	);
};

export default EmailFamilies;
